import _ from "underscore";

import WorkspaceAPI from "../api/api/WorkspaceAPI";
import { getUserWorkspacePermissions, updateTenantUsers } from "./UserUtils";
import { QueryClient } from "@tanstack/react-query";

export const handleWorkspaceResponse = (
  workspace,
  workspaceID,
  workspacePermissions,
  users,
  user,
  httpStatus = null
) => {
  if (httpStatus === 206) {
    return {
      workspace,
      workspaceMembers: [],
      workspaceData: { memberIds: [], isMember: false, isOwner: false },
      partialAccess: true,
    };
  }

  const members = workspace?.Members;

  if (!members) return new Error("No members found in workspace");

  let workspaceMembers = members?.map((mem) => {
    mem.Data = _.findWhere(users, { id: mem.PersonID });
    const permissionProfile = mem.Permissions.WorkspaceProfile;
    const permissions = getUserWorkspacePermissions(
      _,
      _,
      workspaceID,
      workspacePermissions,
      permissionProfile
    );

    mem.Permissions.WorkspaceProfile = permissions;

    return mem;
  });

  const memberIds = workspace.Members.map((member) => member.PersonID);
  const isMember = memberIds.includes(user?.id);
  const isOwner = workspace.Owner === user?.id;

  const workspaceData = { memberIds, isMember, isOwner };

  return {
    workspace,
    workspaceMembers,
    workspaceData,
    partialAccess: false,
  };
};

const updateWorkspacesGroup = (workspaceID, workspaces, workspace) => {
  let workspaceIndex = _.findIndex(workspaces, { id: workspaceID });
  if (workspaceIndex > -1) {
    workspaces[workspaceIndex] = workspace;
  }
};

// Uses QueryClient to update and other data to update the state of workspaces
/**
 *
 * @param {*} data
 * @param {QueryClient} queryClient
 * @param {string} workspaceID
 * @param {*} workspacePermissions
 * @param {*} users
 * @param {string} tenantID
 */
export const handleWorkspacesUserUpdate = (
  data,
  queryClient,
  workspaceID,
  workspacePermissions,
  users,
  tenantID
) => {
  let { user, updateWorkspace, workspace } = data.data.data;

  let currentUser = queryClient.getQueryData(["user", tenantID])?.user;

  if (currentUser.id === user.id) {
    queryClient.setQueryData(["user", tenantID], (prev) => ({
      ...(prev || {}),
      user,
    }));
  }

  updateTenantUsers(queryClient, tenantID, user);

  // To update the permissions of the user in the users list.
  users = users.map((u) => {
    if (u.id === user.id) return user;

    return u;
  });

  users.push(user);

  queryClient.setQueryData(["workspace", workspaceID], () => {
    const result = handleWorkspaceResponse(
      updateWorkspace || workspace,
      workspaceID,
      workspacePermissions,
      users,
      currentUser,
      data.data.httpStatus
    );

    return result;
  });

  queryClient.invalidateQueries(["workspaces", tenantID]);

  // TODO: Should we update the people data as well here?
};

export const handleWorkspacesUpdate = (workspaces, user) => {
  const workspaceIds = workspaces.map((w) => w.id);

  const userOwnedWorkspaces = [];
  const userCollabWorkspaces = [];
  const publicWorkspaces = [];
  let defaultWorkspace;

  // TODO: Update this when the endpoint changes to return only the workspaces the user is a member of
  for (const workspace of workspaces) {
    if (workspace?.Default === true) {
      defaultWorkspace = workspace;
    }

    if (workspace.Owner === user?.id) {
      workspace.isMember = true;
      userOwnedWorkspaces.push(workspace);
      continue;
    }

    const memberIds = workspace.Members.map((member) => member.PersonID);

    if (memberIds.includes(user?.id)) {
      workspace.isMember = true;
      userCollabWorkspaces.push(workspace);
      continue;
    }

    if (workspace?.Privacy !== "private") {
      publicWorkspaces.push(workspace);
    }
  }

  return {
    defaultWorkspace,
    workspaces,
    workspaceIds,
    userOwnedWorkspaces,
    userCollabWorkspaces,
    publicWorkspaces,
  };
};

// Handles converting Sidebar tree to array of sidebar items
const convertSidebarTree = (tree) => {
  const { rootId, items } = tree;
  const sidebarItems = [];

  for (const itemId of items[rootId].children) {
    const item = items[itemId];
    const type = item.data.Type;

    if (item.data.Type === "Empty" || item.data.Type === "EmptySidebar")
      continue;

    if (!["Group", "CloudAppOwner", "CloudAppViewer"].includes(type)) {
      sidebarItems.push({
        ...item.data,
        id: item.id,
      });
    } else {
      const groupItem = {
        ...item.data,
        id: item.id,
        Items: [],
      };

      for (const childId of item.children) {
        const child = items[childId];
        if (child.data.Type === "Empty" || child.data.Type === "EmptySidebar") {
          continue;
        }

        groupItem.Items.push({
          ...child.data,
          id: child.id,
        });
      }

      sidebarItems.push(groupItem);
    }
  }

  return sidebarItems;
};

export const saveTree = async (
  tree,
  workspaceID,
  tenantID,
  queryClient,
  appUpdated = null // For when apps are reordered in the sidebar
) => {
  const sidebarItems = convertSidebarTree(tree);

  await WorkspaceAPI.updateWorkspace(workspaceID, {
    Sidebar: sidebarItems,
    ...(!!appUpdated ? { AppUpdated: appUpdated } : {}),
  });

  queryClient.invalidateQueries(["workspaces", tenantID]);
};
